/**
 * LLM Fix Repository
 *
 * FERP CRITICAL: Repository pattern for LLM fix assistant operations
 * All GraphQL calls routed through repository (no direct API calls)
 *
 * Principles:
 * - Repository pattern strict enforcement
 * - Type-safe GraphQL operations
 * - Error handling with context
 * - Audit trail preservation
 */

import { ApolloClient } from '@apollo/client';
import {
  REQUEST_LLM_FIX,
  APPLY_LLM_FIX,
  REJECT_LLM_FIX,
  GET_LLM_FIX_RECOMMENDATION,
  LIST_LLM_FIX_RECOMMENDATIONS,
  type LLMFixRecommendation,
  type ApplyFixResult,
  type RequestLLMFixInput,
  type ApplyLLMFixInput,
  type RejectLLMFixInput,
} from '../../graphql/llmFixOperations';

export class LLMFixRepository {
  constructor(private client: ApolloClient) {}

  /**
   * Request LLM fix recommendations for validation errors
   */
  async requestFix(
    templateId: string,
    tenantId: string,
    requestedBy: string
  ): Promise<LLMFixRecommendation> {
    const input: RequestLLMFixInput = {
      template_id: templateId,
      tenant_id: tenantId,
      requested_by: requestedBy,
    };

    const { data, error } = await this.client.mutate<Record<string, any>>({
      mutation: REQUEST_LLM_FIX,
      variables: { input },
    });

    if (error) {
      throw new Error(`Failed to request LLM fixes: ${error.message}`);
    }

    if (!data?.requestLLMFix) {
      throw new Error('No fix recommendations returned from server');
    }

    return data?.requestLLMFix ?? null;
  }

  /**
   * Apply accepted fixes and re-validate template
   */
  async applyFix(
    recommendationId: string,
    templateId: string,
    tenantId: string,
    appliedBy: string,
    fixIndices?: number[]
  ): Promise<ApplyFixResult> {
    const input: ApplyLLMFixInput = {
      recommendation_id: recommendationId,
      template_id: templateId,
      tenant_id: tenantId,
      applied_by: appliedBy,
      fix_indices: fixIndices,
    };

    const { data, error } = await this.client.mutate<Record<string, any>>({
      mutation: APPLY_LLM_FIX,
      variables: { input },
    });

    if (error) {
      throw new Error(`Failed to apply LLM fixes: ${error.message}`);
    }

    if (!data?.applyLLMFix) {
      throw new Error('No apply result returned from server');
    }

    return data?.applyLLMFix ?? null;
  }

  /**
   * Reject fix recommendations with optional reason
   */
  async rejectFix(
    recommendationId: string,
    templateId: string,
    tenantId: string,
    rejectionReason?: string
  ): Promise<boolean> {
    const input: RejectLLMFixInput = {
      recommendation_id: recommendationId,
      template_id: templateId,
      tenant_id: tenantId,
      rejection_reason: rejectionReason,
    };

    const { data, error } = await this.client.mutate<Record<string, any>>({
      mutation: REJECT_LLM_FIX,
      variables: { input },
    });

    if (error) {
      throw new Error(`Failed to reject LLM fixes: ${error.message}`);
    }

    return data?.rejectLLMFix ?? false;
  }

  /**
   * Get fix recommendation by ID
   */
  async getRecommendation(
    recommendationId: string,
    templateId: string,
    tenantId: string
  ): Promise<LLMFixRecommendation | null> {
    const { data, error } = await this.client.query<Record<string, any>>({
      query: GET_LLM_FIX_RECOMMENDATION,
      variables: {
        recommendationId,
        templateId,
        tenantId,
      },
      fetchPolicy: 'network-only', // Always fetch fresh data
    });

    if (error) {
      throw new Error(`Failed to get LLM fix recommendation: ${error.message}`);
    }

    return data?.getLLMFixRecommendation ?? null;
  }

  /**
   * List all recommendations for a template
   */
  async listRecommendations(
    templateId: string,
    tenantId: string
  ): Promise<LLMFixRecommendation[]> {
    const { data, error } = await this.client.query<Record<string, any>>({
      query: LIST_LLM_FIX_RECOMMENDATIONS,
      variables: {
        templateId,
        tenantId,
      },
      fetchPolicy: 'network-only',
    });

    if (error) {
      throw new Error(`Failed to list LLM fix recommendations: ${error.message}`);
    }

    return data?.listLLMFixRecommendations ?? [];
  }
}

/**
 * Factory function to create LLMFixRepository
 * Use this in hooks and components
 */
export const createLLMFixRepository = (client: ApolloClient): LLMFixRepository => {
  return new LLMFixRepository(client);
};

/**
 * Export default repository instance (for testing)
 */
export default LLMFixRepository;
